import { assignInlineVars } from "@vanilla-extract/dynamic";
import { ReactElement } from "react";

import { Divider } from "../divider/divider";
import { PaymentMethodTitle } from "../payment-method-title/payment-method-title";
import { RadioTile } from "../radio-tile/radio-tile";
import { gapVar, radioGroupRootClass } from "./radio-group.css";

export interface RadioOption {
  id: string;
  type?: string;
  label?: string;
  labelComponent?: ReactElement;
  leadingSlot?: ReactElement;
  trailingSlot?: ReactElement;
  messages?: string[];
}

export interface RadioGroupProps {
  /**
   * Gap between radio elements
   */
  gap?: string;
  /**
   * List of options
   */
  options: RadioOption[];
  /**
   * Selected option
   */
  selectedOption?: string;
  /**
   * children of selected option in radio group
   */
  children?: ReactElement;
  /**
   * Tab index
   */
  tabIndex?: number;
  /**
   * To show border around radio button
   */
  onChange: (s: string, type: string) => void;
  /**
   * isExpanded to control visibility of children from parent component, even if option is selected
   */
  isExpanded?: boolean;
  /**
   * Resetting animation whenever the string changes
   */
  resetAnimation?: string;
  /**
   * Divider Index
   */
  dividerIndex?: number;
  /**
   * Divider Text
   */
  dividerText?: string;
}

export const RadioGroup = ({
  gap = "4",
  options,
  selectedOption,
  children,
  tabIndex,
  isExpanded = true,
  resetAnimation,
  onChange,
  dividerIndex,
  dividerText,
}: RadioGroupProps) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentElement = e.target as HTMLDivElement;
    const optionIds = options.map((option) => `${option.id}Option`);
    const index = options.findIndex((option) => option.id === selectedOption);
    if (optionIds.includes(currentElement.id)) {
      switch (e.code) {
        case "ArrowLeft":
        case "ArrowUp": {
          const previousOptionIndex = index - 1;
          if (previousOptionIndex >= 0) {
            const { id, type } = options[previousOptionIndex];
            onChange(id, type ?? "");

            const sibling =
              currentElement?.parentElement?.parentElement?.parentElement
                ?.previousElementSibling;

            if (sibling?.childNodes?.length === 2) {
              (
                sibling?.children[1]?.firstChild?.firstChild as HTMLDivElement
              )?.focus();
            } else {
              (
                sibling?.firstChild?.firstChild?.firstChild as HTMLDivElement
              )?.focus();
            }
          }
          break;
        }
        case "ArrowDown":
        case "ArrowRight": {
          const nextOptionIndex = index + 1;
          if (nextOptionIndex < options.length) {
            const { id, type } = options[nextOptionIndex];
            onChange(id, type ?? "");

            const sibling =
              currentElement?.parentElement?.parentElement?.parentElement
                ?.nextElementSibling;

            if (sibling?.childNodes?.length === 2) {
              (
                sibling?.children[1]?.firstChild?.firstChild as HTMLDivElement
              )?.focus();
            } else {
              (
                sibling?.firstChild?.firstChild?.firstChild as HTMLDivElement
              )?.focus();
            }
          }
          break;
        }
        default:
          return;
      }
    }
  };

  let DividerComponent = null;

  return (
    <div
      className={radioGroupRootClass}
      style={assignInlineVars({
        [gapVar]: gap,
      })}
    >
      {options.map((option, index) => {
        if (index === dividerIndex) {
          DividerComponent = <Divider label={dividerText} />;
        } else {
          DividerComponent = null;
        }

        return (
          <span key={`${option.id}Option`}>
            {DividerComponent}
            <RadioTile
              id={`${option.id}Option`}
              tabIndex={tabIndex}
              onChange={onChange}
              onKeyDown={onKeyDown}
              messages={option.messages}
              title={
                <PaymentMethodTitle
                  title={option.label}
                  titleStyleVariant={"heading4"}
                  titleComponent={option.labelComponent}
                  leadingSlot={option.leadingSlot}
                  trailingSlot={option.trailingSlot}
                  forId={`${option.id}Option`}
                />
              }
              type={option.type}
              value={option.id}
              isSelected={option.id === selectedOption}
              showChildren={isExpanded}
              resetAnimation={resetAnimation}
            >
              {selectedOption === option.id ? children : null}
            </RadioTile>
          </span>
        );
      })}
    </div>
  );
};

RadioGroup.displayName = "RadioGroup";
